import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { FounderClientRedirect } from "@/components/FounderClientRedirect";
import Index from "./pages/Index";
import InventoryPage from "./pages/InventoryPage";
import SalesPage from "./pages/SalesPage";
import ExpensesPage from "./pages/ExpensesPage";
import SuppliersPage from "./pages/SuppliersPage";
import TransportLogisticsPage from "./pages/TransportLogisticsPage";
import ManualEntryPage from "./pages/ManualEntryPage";
import MasterLedgerPage from "./pages/MasterLedgerPage";
import TeamManagementPage from "./pages/TeamManagementPage";
import SystemLogsPage from "./pages/SystemLogsPage";
import ReportsPage from "./pages/ReportsPage";
import MyActivityPage from "./pages/MyActivityPage";
import AuthPage from "./pages/AuthPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AuthConfirmPage from "./pages/AuthConfirmPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import UserGuidePage from "./pages/UserGuidePage";
import FounderDashboardPage from "./pages/FounderDashboardPage";
import PricingPage from "./pages/PricingPage";
import LocationsPage from "./pages/LocationsPage";
import BusinessIntelligencePage from "./pages/BusinessIntelligencePage";
import StaffPerformancePage from "./pages/StaffPerformancePage";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import { PRODUCTION_ORIGIN } from "@/constants";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { MFAChallengeScreen } from "@/components/MFAChallengeScreen";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionPlan } from "@/hooks/useSubscriptionPlan";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";

/** Transport & Logistics is Pro-only. Free users get paywall and redirect. */
function TransportLogisticsGate() {
  const plan = useSubscriptionPlan();
  const { openPaywall } = usePaywallModal();
  useEffect(() => {
    if (plan === "free") openPaywall("transport_logistics");
  }, [plan, openPaywall]);
  if (plan === "free") return <Navigate to="/" replace />;
  return <TransportLogisticsPage />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 min — keep cached data fresh for offline use
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => (error as { name?: string })?.name === "AbortError" ? false : failureCount < 2,
    },
  },
});

/** Redirect to canonical domain (app.reviewboost24.com) when on Vercel default URL so one source of truth. */
function CanonicalRedirect() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname.toLowerCase();
    const isVercelDefault =
      host.endsWith(".vercel.app") ||
      (host.includes("reviewboost-pro-manager") && host !== "app.reviewboost24.com");
    if (isVercelDefault) {
      const target = `${PRODUCTION_ORIGIN}${location.pathname}${location.search}${location.hash}`;
      window.location.replace(target);
    }
  }, [location.pathname, location.search, location.hash]);
  return null;
}

function MainApp() {
  const { user, loading, role, isFounder } = useAuth();
  const [mfaRequired, setMfaRequired] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fallback = window.setTimeout(() => {
      if (!cancelled) setMfaRequired(false);
    }, 8000);
    supabase.auth.mfa
      .getAuthenticatorAssuranceLevel()
      .then(({ data }) => {
        if (cancelled) return;
        window.clearTimeout(fallback);
        if (!data) {
          setMfaRequired(false);
          return;
        }
        const d = data as { current_level?: string; next_level?: string; currentLevel?: string; nextLevel?: string };
        const current = d.current_level ?? d.currentLevel;
        const next = d.next_level ?? d.nextLevel;
        const needsMfa = next === "aal2" && current !== "aal2";
        setMfaRequired(needsMfa);
      })
      .catch(() => {
        window.clearTimeout(fallback);
        if (!cancelled) setMfaRequired(false);
      });
    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
    };
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <AuthPage />;

  if (mfaRequired === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (mfaRequired) {
    return (
      <MFAChallengeScreen
        onSuccess={() => {
          setMfaRequired(false);
        }}
      />
    );
  }

  return (
    <DashboardLayout>
      <FounderClientRedirect>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route
          path="/inventory"
          element={
            <ProtectedRoute requireAdmin>
              <InventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRoute requireAdmin>
              <SalesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/manual-entry" element={<ManualEntryPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute requireAdmin>
              <SuppliersPage />
            </ProtectedRoute>
          }
        />
        <Route path="/transport-logistics" element={<TransportLogisticsGate />} />
        <Route
          path="/master-ledger"
          element={
            <ProtectedRoute requireAdmin>
              <MasterLedgerPage />
            </ProtectedRoute>
          }
        />
        <Route path="/my-activity" element={<MyActivityPage />} />
        <Route
          path="/team"
          element={
            <ProtectedRoute requireAdmin>
              <TeamManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-logs"
          element={
            <ProtectedRoute requireAdmin>
              <SystemLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requireAdmin>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock-intelligence"
          element={
            <ProtectedRoute requireAdmin>
              <BusinessIntelligencePage />
            </ProtectedRoute>
          }
        />
        <Route path="/business-intelligence" element={<Navigate to="/stock-intelligence" replace />} />
        <Route
          path="/staff-performance"
          element={
            <ProtectedRoute requireAdmin>
              <StaffPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/businesses"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="businesses" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/revenue"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="revenue" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bug-reports"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="bug-reports" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/broadcasts"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="broadcasts" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-log"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="audit-log" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="settings" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/plan-overrides"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="plan-overrides" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feature-flags"
          element={
            <ProtectedRoute requireFounder>
              <FounderDashboardPage section="feature-flags" />
            </ProtectedRoute>
          }
        />
        <Route path="/pricing" element={<PricingPage />} />
        <Route
          path="/locations"
          element={
            <ProtectedRoute requireAdmin>
              <LocationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/settings/:section?" element={<Settings />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </FounderClientRedirect>
    </DashboardLayout>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/confirm" element={<AuthConfirmPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/user-guide" element={<UserGuidePage />} />
      <Route path="*" element={<MainApp />} />
    </Routes>
  );
}

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ThemeProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <CanonicalRedirect />
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
